/**
 * PostDeployAutoVerifyCard — iter84.59.14 Task 3.
 *
 * In-product Post-Deploy Auto-Verify. Replaces the manual-walkthrough
 * markdown UATs with a one-click programmatic verifier.
 *
 * Calls POST /api/site-admin/post-deploy/auto-verify and renders a
 * tile grid (pass / warn / fail). Each tile shows the check name,
 * status pill, and an actionable detail string. Designed to be opened
 * AFTER a production deploy lands so the platform admin can confirm
 * operator-facing flows still work without copy-pasting markdown.
 *
 * Sibling of `DeploymentReadinessBanner` (which is the PRE-deploy
 * version). The two will eventually share a render layer; for now
 * this card is intentionally small + self-contained so the upgrade
 * path is reversible.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { CopyDiagButton, buildDiagText, diagLine } from "@/components/site-admin/release-ops/CopyDiagButton";
import { WhatsThisButton } from "@/components/site-admin/release-ops/WhatsThisButton";
import {
    CheckCircle2, AlertTriangle, XCircle, RefreshCw, ShieldCheck, Clock,
} from "lucide-react";

const STATUS_META = {
    pass: { Icon: CheckCircle2, color: "text-emerald-400", border: "border-emerald-500/40", bg: "bg-emerald-500/5", pill: "PASS", pillBg: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
    warn: { Icon: AlertTriangle, color: "text-amber-400",   border: "border-amber-500/40",   bg: "bg-amber-500/5",   pill: "WARN", pillBg: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
    fail: { Icon: XCircle,        color: "text-rose-400",    border: "border-rose-500/40",    bg: "bg-rose-500/5",    pill: "FAIL", pillBg: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
};

const OVERALL_META = {
    pass: { label: "Healthy",       color: "text-emerald-300", pillBg: "bg-emerald-500/15 border-emerald-500/40" },
    warn: { label: "Needs review",  color: "text-amber-300",   pillBg: "bg-amber-500/15 border-amber-500/40" },
    fail: { label: "Action needed", color: "text-rose-300",    pillBg: "bg-rose-500/15 border-rose-500/40" },
};

function CheckTile({ check }) {
    const meta = STATUS_META[check.status] || STATUS_META.warn;
    const { Icon } = meta;
    return (
        <div
            data-testid={`post-deploy-check-${check.key}`}
            data-status={check.status}
            className={`brutal-card-light p-3 ${meta.border} ${meta.bg} flex gap-2`}
        >
            <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${meta.color}`} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-zinc-200 uppercase tracking-wider">
                        {check.name}
                    </span>
                    <span className={`pill text-[9px] py-0 px-1.5 border ${meta.pillBg}`}>
                        {meta.pill}
                    </span>
                </div>
                <p className="text-[11px] text-zinc-400 mt-1 leading-snug">{check.detail}</p>
                {check.affected_tournament_ids?.length > 0 && (
                    <p className="text-[10px] text-zinc-500 mt-1 font-mono">
                        Affected: {check.affected_tournament_ids.join(", ")}
                    </p>
                )}
            </div>
        </div>
    );
}

export default function PostDeployAutoVerifyCard({ onVerdictResolved, autoRun = false }) {
    const [result, setResult] = useState(null);
    // iter86.29 — env-aware copy: this same card renders on both planes.
    const [rehearsal, setRehearsal] = useState(true);
    useEffect(() => {
        api.platformEnvironment()
            .then((r) => setRehearsal(r.mode !== "data_plane"))
            .catch(() => {});
    }, []);
    const [busy, setBusy] = useState(false);
    const [lastRunAt, setLastRunAt] = useState(null);

    const runVerify = useCallback(async () => {
        setBusy(true);
        try {
            const r = await api.postDeployAutoVerify();
            setResult(r);
            setLastRunAt(new Date());
            const meta = OVERALL_META[r.overall] || OVERALL_META.warn;
            if (r.overall === "pass") {
                toast.success(`Auto-verify: ${meta.label} — all ${r.pass_count} check(s) passed`);
            } else if (r.overall === "warn") {
                toast.warning(`Auto-verify: ${r.warn_count} warning(s)`);
            } else {
                toast.error(`Auto-verify: ${r.fail_count} failure(s) — action needed`);
            }
            // iter84.59.15 — Lift verdict so the parent ReleaseOpsTab can
            // auto-flip the Phase 1 `auto_verify_preview` gate (preview only).
            // Map this card's vocabulary to the shared autoFlipGate helper:
            //   pass → "clear" (Approve), warn → "warn" (Waive with count),
            //   fail → "fail" (leave Pending so operator must investigate).
            if (onVerdictResolved) {
                const verdict =
                    r.overall === "pass" ? "clear" :
                    r.overall === "warn" ? "warn" :
                    "fail";
                onVerdictResolved({
                    overall: verdict,
                    summary: { warn_count: r.warn_count || 0, fail_count: r.fail_count || 0 },
                    environment: r.environment,
                    iter: r.iter,
                });
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || "Auto-verify failed";
            toast.error(msg);
            setResult({ error: msg });
        }
        setBusy(false);
    }, [onVerdictResolved]);

    // iter86.82.3 — autoRun: fire the read-only battery once on mount so
    // the card lands with a verdict (parity with the readiness banner and
    // vendor card, which auto-fetch). Used on production surfaces
    // (Concert Health, Phase 5); preview Phase 1 stays click-to-run.
    useEffect(() => {
        if (autoRun) runVerify();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRun]);

    const overallMeta = result?.overall ? (OVERALL_META[result.overall] || OVERALL_META.warn) : null;

    // iter84.71 — Same CollapsibleCard shell as the Production Health /
    // Deployment Readiness banner: verdict-colored outline + status
    // badge in the header, body collapsed until clicked (auto-opens on
    // fail). Requested so the card reads consistently next to the
    // health banner in Phase 5 (production) and Phase 1.5 (preview).
    const badge = result?.error
        ? "ERROR"
        : result
            ? `${(result.overall || "?").toUpperCase()}·${(result.checks || []).length}`
            : "NOT RUN";
    const badgeClass = result?.error || result?.overall === "fail"
        ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
        : result?.overall === "warn"
            ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
            : result?.overall === "pass"
                ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                : "bg-zinc-800 text-zinc-400 border-zinc-600";

    const headerExtra = (
        <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <WhatsThisButton
                testId="post-deploy-whats-this"
                title="Post-Deploy Auto-Verify"
                what={rehearsal
                    ? "A rehearsal of the post-deploy verifier: the same read-only checks production runs after a deploy (database reachable, retired code stayed retired, no tied-KO winner drift, honest data attribution) — exercised against preview data."
                    : "Run this AFTER a deploy finishes. It re-checks known-fragile behaviors against the live app: database reachable, retired code stayed retired, no tied-KO winner drift, honest data attribution."}
                lookFor={rehearsal
                    ? "All pass here means the verifier battery is healthy and ready for the Concert Plane. A FAIL is a preview-data or verifier problem to fix before shipping."
                    : "All pass within a few minutes of deploying. A FAIL means the deploy broke something — hotfix or roll back."}
                notFor={rehearsal
                    ? "Pre-deploy code/config checks (that's Deployment Readiness) or continuous data quality (that's Game Health)."
                    : "Pre-deploy decisions — by the time you run this, you've already shipped."}
            />
            {result && !result.error && (
                <CopyDiagButton
                    testId="post-deploy-copy-diag-btn"
                    getText={() => {
                        const checks = result.checks || [];
                        return buildDiagText({
                            card: "Post-Deploy Auto-Verify",
                            environment: result.environment,
                            verdict: result.overall,
                            summary: `${checks.filter((c) => c.status === "pass").length}/${checks.length} pass`,
                            lines: checks
                                .filter((c) => c.status !== "pass")
                                .map((c) => diagLine(c.status, c.name, [c.detail, c.affected_tournament_ids?.length ? `affected: ${c.affected_tournament_ids.join(", ")}` : ""].filter(Boolean).join(" · "))),
                        });
                    }}
                />
            )}
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); runVerify(); }}
                disabled={busy}
                data-testid="post-deploy-run-btn"
                className="brutal-button px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-1 bg-cyber/15 border-cyber hover:bg-cyber/25"
            >
                <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
                {busy ? "Verifying…" : (result ? "Re-run" : "Run auto-verify")}
            </button>
        </span>
    );

    return (
        <CollapsibleCard
            testId="post-deploy-auto-verify-card"
            icon={ShieldCheck}
            title="Post-Deploy Auto-Verify"
            badge={badge}
            badgeClass={badgeClass}
            verdict={result?.error ? "error" : (result?.overall || null)}
            headerExtra={headerExtra}
            defaultOpen={false}
        >
            <div className="space-y-3" data-overall={result?.overall || "unknown"}>
            <p className="text-[11px] text-zinc-400 leading-snug">
                {rehearsal
                    ? "Rehearsal run of the post-deploy verifier — the same read-only battery of operator-facing flow checks the Concert Plane runs after a deploy lands, exercised here against preview data so you know the verifier itself works before shipping. Zero DB writes, zero live upstream API calls."
                    : "Programmatic verifier. Run after a production deploy lands to confirm operator-facing flows still work. Zero DB writes, zero live upstream API calls."}
            </p>

            {result?.error && (
                <div
                    className="text-xs text-rose-400 flex items-center gap-1 p-2 border border-rose-500/40 rounded bg-rose-500/5"
                    data-testid="post-deploy-error"
                >
                    <XCircle className="w-3 h-3" /> {result.error}
                </div>
            )}

            {result && !result.error && (
                <>
                    {/* Overall verdict strip */}
                    {overallMeta && (
                        <div
                            className={`flex items-center justify-between flex-wrap gap-2 p-2 rounded border ${overallMeta.pillBg}`}
                            data-testid="post-deploy-overall-strip"
                        >
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                                <span className={`font-bold uppercase tracking-wider ${overallMeta.color}`}>
                                    {overallMeta.label}
                                </span>
                                <span className="text-zinc-400" data-testid="post-deploy-overall-counts">
                                    {result.pass_count} pass · {result.warn_count} warn · {result.fail_count} fail
                                </span>
                                {result.environment && result.environment !== "unknown" && (
                                    <span className="pill text-[9px] py-0 px-1.5 bg-zinc-800 border-zinc-600 text-zinc-300 uppercase">
                                        {result.environment}
                                    </span>
                                )}
                                {result.iter && (
                                    <span className="pill text-[9px] py-0 px-1.5 bg-volt/10 border-volt/40 text-volt">
                                        iter{result.iter}
                                    </span>
                                )}
                            </div>
                            {lastRunAt && (
                                <div className="flex items-center gap-1 text-[10px] text-zinc-500">
                                    <Clock className="w-2.5 h-2.5" />
                                    Last run: {lastRunAt.toLocaleTimeString()}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Tile grid */}
                    <div
                        className="grid grid-cols-1 md:grid-cols-2 gap-2"
                        data-testid="post-deploy-check-tiles"
                    >
                        {result.checks?.map(c => (
                            <CheckTile key={c.key} check={c} />
                        ))}
                    </div>
                </>
            )}

            {!result && !busy && (
                <p className="text-[11px] text-zinc-500 italic" data-testid="post-deploy-empty-state">
                    Click "Run auto-verify" to scan: DB reachable, iter84.59.12/13 backend contracts live,
                    no tied-KO drift, current iter UAT present.
                </p>
            )}
            </div>
        </CollapsibleCard>
    );
}
